/**
 * V82.7 Phase 1: IP 화이트리스트 관리 훅 (system_admin 전용)
 */

import { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { getFunctions } from '../../firebase/utils';
import {
  IpWhitelistRecord,
  IpWhitelistType,
  SecurityAuditLogRecord,
} from '../types/ipWhitelist';

interface ListResponse {
  success: boolean;
  items: IpWhitelistRecord[];
  totalCount: number;
}

interface AuditResponse {
  success: boolean;
  items: SecurityAuditLogRecord[];
  totalCount: number;
}

export function useIpWhitelists(includeInactive: boolean = true) {
  const [items, setItems] = useState<IpWhitelistRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const fn = httpsCallable<{ includeInactive?: boolean }, ListResponse>(
        getFunctions(),
        'listIpWhitelists'
      );
      const r = await fn({ includeInactive });
      if (!r.data.success) throw new Error('조회 실패');
      setItems(r.data.items);
    } catch (e: any) {
      console.error('[V82.7] listIpWhitelists 실패:', e);
      setError(new Error(e.message || '조회 실패'));
    } finally {
      setIsLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { items, isLoading, error, refetch };
}

export async function callCreateIpWhitelist(input: {
  ip: string;
  type?: IpWhitelistType;
  cidr?: string;
  label?: string;
  memo?: string;
}): Promise<IpWhitelistRecord> {
  const fn = httpsCallable<typeof input, { success: boolean; id: string; record: IpWhitelistRecord }>(
    getFunctions(),
    'createIpWhitelist'
  );
  const r = await fn(input);
  return r.data.record;
}

export async function callDisableIpWhitelist(input: {
  id: string;
  reason?: string;
  confirmEvenIfLast?: boolean;
}): Promise<{ remainingActiveCount: number }> {
  const fn = httpsCallable<typeof input, { success: boolean; remainingActiveCount: number }>(
    getFunctions(),
    'disableIpWhitelist'
  );
  const r = await fn(input);
  return { remainingActiveCount: r.data.remainingActiveCount };
}

export async function callEnableIpWhitelist(input: { id: string; reason?: string }): Promise<void> {
  const fn = httpsCallable<typeof input, { success: boolean }>(
    getFunctions(),
    'enableIpWhitelist'
  );
  await fn(input);
}

export async function callUpdateIpWhitelistMemo(input: {
  id: string;
  label?: string;
  memo?: string;
}): Promise<void> {
  const fn = httpsCallable<typeof input, { success: boolean }>(
    getFunctions(),
    'updateIpWhitelistMemo'
  );
  await fn(input);
}

export function useIpWhitelistAuditLogs(ipWhitelistId?: string, refreshKey?: number) {
  const [items, setItems] = useState<SecurityAuditLogRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let abort = false;
    (async () => {
      try {
        setIsLoading(true);
        setError(null);
        const fn = httpsCallable<{ ipWhitelistId?: string; limit?: number }, AuditResponse>(
          getFunctions(),
          'listIpWhitelistAuditLogs'
        );
        const r = await fn({ ipWhitelistId, limit: 200 });
        if (abort) return;
        if (!r.data.success) throw new Error('audit 조회 실패');
        setItems(r.data.items);
      } catch (e: any) {
        if (!abort) {
          console.error('[V82.7] listIpWhitelistAuditLogs 실패:', e);
          setError(new Error(e.message || '조회 실패'));
        }
      } finally {
        if (!abort) setIsLoading(false);
      }
    })();
    return () => { abort = true; };
  }, [ipWhitelistId, refreshKey]);

  return { items, isLoading, error };
}
